/**
 * The page dots under a photo gallery, at most `max` of them.
 *
 * One dot per photo ran straight across the bottom of a 20-photo gallery and
 * sat on top of the "Video" button and the "2/20" counter (owner, app
 * screenshot of the Riad cooking class, 2026-10-15). So the dots are a sliding
 * window around the current photo, the way iOS does it: never more than five,
 * and the dot at either end of the window shrinks when there are more photos
 * beyond it. The counter already says the exact number.
 *
 * Pure, so the website and the app share one rule.
 */
export type PageDotSize = "active" | "normal" | "small";

export interface PageDot {
  /** The photo this dot stands for. */
  index: number;
  size: PageDotSize;
}

export const PAGE_DOTS_MAX = 5;

export function pageDots(count: number, active: number, max: number = PAGE_DOTS_MAX): PageDot[] {
  const total = Math.max(0, Math.floor(count));
  // One photo, or none: there is nothing to page through.
  if (total <= 1) return [];
  const limit = Math.max(1, Math.floor(max));
  const current = Math.min(Math.max(0, Math.floor(active)), total - 1);

  if (total <= limit) {
    return Array.from({ length: total }, (_, i) => ({
      index: i,
      size: i === current ? "active" : "normal",
    }));
  }

  // Keep the current photo in the middle of the window where possible.
  const start = Math.min(Math.max(0, current - Math.floor(limit / 2)), total - limit);
  const end = start + limit - 1;
  return Array.from({ length: limit }, (_, k) => {
    const index = start + k;
    let size: PageDotSize = "normal";
    if (index === current) size = "active";
    else if ((index === start && start > 0) || (index === end && end < total - 1)) size = "small";
    return { index, size };
  });
}
